import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import './ConfigureDevice.css';
import CollapsibleSection from '../CollapsibleSection'; // Ensure correct import path
import ConfigureDeviceModal from './ConfigureDeviceModal';
import yaml from 'js-yaml';
import debounce from 'lodash/debounce';
import API_BASE_URL from '../config'; // Centralized configuration
import LoadingIndicator from '../LoadingIndicator'; // Import LoadingIndicator
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ConfigureDevice = () => {
  // Helper: Extract the numeric portion from a string.
  const getNumericValue = (str) => {
    if (!str) return 0;
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  // **Authentication**
  const auth = getAuth();
  const currentUser = auth.currentUser;

  // **State variables for form data**
  const [game, setGame] = useState('');
  const [minBet, setMinBet] = useState('');
  const [maxBet, setMaxBet] = useState('');
  const [submittedMinBet, setSubmittedMinBet] = useState('');
  const [submittedMaxBet, setSubmittedMaxBet] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [games, setGames] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  // **State variables for sidebar selections**
  const [properties, setProperties] = useState([]);
  const [floors, setFloors] = useState([]);
  const [pits, setPits] = useState([]);
  const [tables, setTables] = useState([]); // Tables for the selected pit
  const [allTables, setAllTables] = useState([]); // All tables fetched from API
  const [filteredTables, setFilteredTables] = useState([]); // Tables after filtering
  const [selectedPropertyName, setSelectedPropertyName] = useState('');
  const [selectedFloorType, setSelectedFloorType] = useState('');
  const [selectedPitName, setSelectedPitName] = useState('');
  const [selectedTableId, setSelectedTableId] = useState('');
  const [selectedTableName, setSelectedTableName] = useState('');
  const [tableSID, setTableSID] = useState(''); // Reintroduced tableSID
  const [customerUID, setCustomerUID] = useState(''); // State for customer_uid

  // **State for search functionality**
  const [searchQuery, setSearchQuery] = useState('');
  // When a table is clicked, we set this filter so that only that table shows.
  const [selectedTableFilter, setSelectedTableFilter] = useState('');

  // **State to control which sections are collapsed**
  const [isPropertyCollapsed, setIsPropertyCollapsed] = useState(false);
  const [isFloorCollapsed, setIsFloorCollapsed] = useState(false);
  const [isPitCollapsed, setIsPitCollapsed] = useState(false);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  // **State to control Configure Device modal**
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);

  // **State to control Context Menu**
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [contextMenuType, setContextMenuType] = useState("tableItem");
  const [rightClickedTableId, setRightClickedTableId] = useState(null);

  // **State to control sidebar visibility**
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // **Debounce handler for search input using useRef**
  const debounceSearchRef = useRef();
  if (!debounceSearchRef.current) {
    debounceSearchRef.current = debounce((query) => {
      // When typing, clear any selected table filter.
      setSelectedTableFilter("");
      setSearchQuery(query);
    }, 300);
  }

  // **Handle Search Input Change**
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    debounceSearchRef.current(query);
  };

  // **Loading States**
  const [isLoading, setIsLoading] = useState(false); // General loading
  const [isLoadingTables, setIsLoadingTables] = useState(false); // Table-specific loading
  const [showLoading, setShowLoading] = useState(false); // Delayed loading indicator
  const loadingTimerRef = useRef(null); // Reference to the timer

  // **Effect to Manage Delayed Loading Indicator**
  useEffect(() => {
    if (isLoading || isLoadingTables) {
      loadingTimerRef.current = setTimeout(() => {
        setShowLoading(true);
      }, 650); // 650ms delay
    } else {
      clearTimeout(loadingTimerRef.current);
      setShowLoading(false);
    }
    return () => {
      clearTimeout(loadingTimerRef.current);
    };
  }, [isLoading, isLoadingTables]);

  // **Handle Highlighted Row**
  const [highlightedRow, setHighlightedRow] = useState(null);

  // **Sort Configuration**
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // **Pagination State Variables for Main Table**
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const totalPages = Math.ceil(filteredTables.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTables = filteredTables.slice(indexOfFirstItem, indexOfLastItem);

  // **Pagination State Variables for Sidebar Tables**
  const [sidebarTablePage, setSidebarTablePage] = useState(1);
  const sidebarItemsPerPage = 10; // 10 tables per page in the sidebar
  const totalSidebarPages = Math.ceil(tables.length / sidebarItemsPerPage);
  const indexOfLastSidebarItem = sidebarTablePage * sidebarItemsPerPage;
  const indexOfFirstSidebarItem = indexOfLastSidebarItem - sidebarItemsPerPage;
  const paginatedSidebarTables = tables.slice(indexOfFirstSidebarItem, indexOfLastSidebarItem);

  // **Handle page change for main table**
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // **Reset main table page when filteredTables changes**
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredTables]);

  // **State for active filters**
  const [activeFilters, setActiveFilters] = useState({
    propertyName: '',
    floorType: '',
    pitName: ''
  });

  // **Fetch games**
  const fetchGames = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/games`);
      console.log('Fetched games:', response.data);
      setGames(response.data);
    } catch (error) {
      console.error('Error fetching games:', error);
      toast.error('Failed to fetch games. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // **Fetch properties**
  const fetchProperties = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!currentUser) {
        console.error('User is not authenticated');
        toast.error('User is not authenticated.');
        return;
      }
      const token = await currentUser.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProperties(response.data);
      console.log('Fetched Properties:', response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch properties. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // **Get Customer UID**
  const getCustomerUID = useCallback(async (customer_id) => {
    try {
      if (!customer_id) return null;
      const response = await axios.get(`${API_BASE_URL}/customerUID`, {
        params: { customer_id },
      });
      return response.data.customer_uid;
    } catch (error) {
      console.error('Error fetching customer UID:', error);
      toast.error('Failed to fetch customer UID.');
      return null;
    }
  }, []);

  // **Handle File Operations**
  const checkAndHandleFile = useCallback(
    async (bucket, property_name, floor_type, floor_name, table_sid) => {
      setIsLoading(true);
      try {
        if (!bucket || !property_name || !floor_type || !floor_name || !table_sid) {
          console.error('Missing parameters for file handling.');
          toast.error('Missing parameters for file handling.');
          return;
        }
        const filePath = `${property_name}/${floor_type}/${floor_name}/${table_sid}/limits.yaml`;
        const checkResponse = await axios.get(`${API_BASE_URL}/checkFile`, {
          params: { bucket, filePath },
        });
        if (checkResponse.data.exists) {
          const readResponse = await axios.get(`${API_BASE_URL}/readFile`, {
            params: { bucket, filePath },
          });
          const yamlContent = readResponse.data.content;
          const limitsData = yaml.load(yamlContent);
          setGame(limitsData.game_name || '');
          setMinBet(limitsData.min_bet || '');
          setMaxBet(limitsData.max_bet || '');
          setIsOnline(limitsData.is_online || false);
          setSubmittedMinBet(limitsData.min_bet || '');
          setSubmittedMaxBet(limitsData.max_bet || '');
        } else {
          const defaultContent = yaml.dump({
            game_name: '',
            min_bet: 10,
            max_bet: 1000,
            is_online: false,
          });
          await axios.post(`${API_BASE_URL}/createFile`, {
            bucket,
            filePath,
            content: defaultContent,
          });
          setGame('');
          setMinBet(10);
          setMaxBet(1000);
          setIsOnline(false);
          setSubmittedMinBet(10);
          setSubmittedMaxBet(1000);
          toast.success('Created default limits.yaml configuration.');
        }
      } catch (error) {
        console.error('Error handling limits.yaml:', error);
        toast.error('Failed to load or create limits configuration.');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // **Fetch all tables across all properties**
  const fetchAllTables = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!currentUser) {
        console.error('User is not authenticated');
        toast.error('User is not authenticated.');
        return;
      }
      const token = await currentUser.getIdToken(true);
      const propertiesResponse = await axios.get(`${API_BASE_URL}/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allProperties = propertiesResponse.data;
      console.log('Fetched Properties:', allProperties);
      const tableMap = new Map();
      for (const property of allProperties) {
        const propertyName = property.property_name;
        const propertyId = property.property_id;
        const customerId = property.customer_id;
        const floorsResponse = await axios.get(`${API_BASE_URL}/floors`, {
          params: { property_name: propertyName },
          headers: { Authorization: `Bearer ${token}` },
        });
        const floors = floorsResponse.data;
        for (const floor of floors) {
          const floorType = floor.floor_type;
          const pitsResponse = await axios.get(`${API_BASE_URL}/pits`, {
            params: { floor_type: floorType, property_name: propertyName },
            headers: { Authorization: `Bearer ${token}` },
          });
          const pits = pitsResponse.data;
          for (const pit of pits) {
            const floorName = pit.floor_name;
            const tablesResponse = await axios.get(`${API_BASE_URL}/tables`, {
              params: { floor_type: floorType, floor_name: floorName, property_name: propertyName },
              headers: { Authorization: `Bearer ${token}` },
            });
            const fetchedTables = tablesResponse.data;
            for (const table of fetchedTables) {
              table.property_id = propertyId;
              table.property_name = propertyName;
              table.customer_id = customerId;
              table.floor_type = floorType;
              table.floor_name = floorName;
              if (!tableMap.has(table.table_id)) {
                tableMap.set(table.table_id, table);
              } else {
                console.log(`Duplicate table found: ${table.table_id}`);
              }
            }
          }
        }
      }
      const combinedTables = Array.from(tableMap.values());
      setAllTables(combinedTables);
      setFilteredTables(combinedTables); // Initially display all tables
      console.log('Fetched All Unique Tables:', combinedTables);
    } catch (error) {
      console.error('Error fetching all tables:', error.response ? error.response.data : error.message);
      toast.error('Failed to fetch tables. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // **Fetch floors**
  const fetchFloors = useCallback(
    async (property_name) => {
      setIsLoading(true);
      try {
        if (!currentUser) {
          console.error('User is not authenticated');
          toast.error('User is not authenticated.');
          return;
        }
        const token = await currentUser.getIdToken(true);
        const response = await axios.get(`${API_BASE_URL}/floors`, {
          params: { property_name },
          headers: { Authorization: `Bearer ${token}` },
        });
        setFloors(response.data);
        console.log('Fetched Floors:', response.data);
      } catch (error) {
        console.error('Error fetching floors:', error);
        toast.error('Failed to fetch floors. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser]
  );

  // **Fetch pits**
  const fetchPits = useCallback(
    async (floor_type, property_name) => {
      setIsLoading(true);
      try {
        if (!currentUser) {
          console.error('User is not authenticated');
          toast.error('User is not authenticated.');
          return;
        }
        const token = await currentUser.getIdToken(true);
        const response = await axios.get(`${API_BASE_URL}/pits`, {
          params: { floor_type, property_name },
          headers: { Authorization: `Bearer ${token}` },
        });
        setPits(response.data);
        console.log('Fetched Pits:', response.data);
      } catch (error) {
        console.error('Error fetching pits:', error);
        toast.error('Failed to fetch pits. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser]
  );

  // **Fetch tables for a specific pit (Sorted Numerically)**
  const fetchTables = useCallback(
    async (floor_type, floor_name, property_name) => {
      setIsLoadingTables(true);
      try {
        if (!currentUser) {
          console.error('User is not authenticated');
          toast.error('User is not authenticated.');
          return;
        }
        const token = await currentUser.getIdToken(true);
        const response = await axios.get(`${API_BASE_URL}/tables`, {
          params: { floor_type, floor_name, property_name },
          headers: { Authorization: `Bearer ${token}` },
        });
        // Sort the tables numerically based on the extracted number from table_name.
        const sortedTables = response.data.sort((a, b) => {
          return getNumericValue(a.table_name) - getNumericValue(b.table_name);
        });
        setTables(sortedTables);
        console.log('Fetched Tables:', sortedTables);
        setSidebarTablePage(1);
      } catch (error) {
        console.error('Error fetching tables:', error.response ? error.response.data : error.message);
        toast.error('Failed to fetch tables. Please try again.');
      } finally {
        setIsLoadingTables(false);
      }
    },
    [currentUser]
  );

  // **Effect to fetch data on component mount**
  useEffect(() => {
    fetchGames();
    fetchProperties();
    fetchAllTables();
  }, [fetchGames, fetchProperties, fetchAllTables]);

  // **Handle Property Selection**
  const handlePropertySelection = (propertyName) => {
    setActiveFilters((prevFilters) => {
      if (prevFilters.propertyName === propertyName) {
        setSelectedPropertyName('');
        setSelectedFloorType('');
        setSelectedPitName('');
        setFloors([]);
        setPits([]);
        setTables([]);
        setIsPropertyCollapsed(false);
        return { propertyName: '', floorType: '', pitName: '' };
      } else {
        setSelectedPropertyName(propertyName);
        setSelectedFloorType('');
        setSelectedPitName('');
        setFloors([]);
        setPits([]);
        setTables([]);
        setIsPropertyCollapsed(true);
        setIsFloorCollapsed(false);
        fetchFloors(propertyName);
        return { propertyName, floorType: '', pitName: '' };
      }
    });
  };

  // **Handle Floor Selection**
  const handleFloorSelection = (floorType) => {
    setActiveFilters((prevFilters) => {
      if (prevFilters.floorType === floorType) {
        setSelectedFloorType('');
        setSelectedPitName('');
        setPits([]);
        setTables([]);
        setIsFloorCollapsed(false);
        return { ...prevFilters, floorType: '', pitName: '' };
      } else {
        setSelectedFloorType(floorType);
        setSelectedPitName('');
        setPits([]);
        setTables([]);
        setIsFloorCollapsed(true);
        setIsPitCollapsed(false);
        fetchPits(floorType, activeFilters.propertyName);
        return { ...prevFilters, floorType, pitName: '' };
      }
    });
  };

  // **Handle Pit Selection**
  const handlePitSelection = (pitName) => {
    setActiveFilters((prevFilters) => {
      if (prevFilters.pitName === pitName) {
        setSelectedPitName('');
        setTables([]);
        setIsPitCollapsed(false);
        return { ...prevFilters, pitName: '' };
      } else {
        setSelectedPitName(pitName);
        setTables([]);
        setIsPitCollapsed(true);
        fetchTables(activeFilters.floorType, pitName, activeFilters.propertyName);
        return { ...prevFilters, pitName };
      }
    });
  };

  // **Handle Table Selection**
  const handleTableSelection = async (tableId, tableName) => {
    console.log('Selected Table:', tableName);
    setSelectedTableId(tableId);
    setSelectedTableName(tableName);
    // Set the filter so that only this table appears in the main list.
    setSelectedTableFilter(tableId);
    setIsPropertyCollapsed(true);
    setIsFloorCollapsed(true);
    setIsPitCollapsed(true);
    setIsTableCollapsed(true);
    setIsLoadingTables(true);
    console.log('Starting table data fetch...');
    try {
      const selectedTableData = allTables.find((table) => table.table_id === tableId);
      if (selectedTableData) {
        const { table_sid, customer_id, customer_uid, property_name, floor_type: tableFloorType, floor_name } = selectedTableData;
        setTableSID(table_sid);
        setCustomerUID(customer_uid);
        if (customer_id) {
          const customer_uid_fetched = await getCustomerUID(customer_id);
          if (customer_uid_fetched) {
            setCustomerUID(customer_uid_fetched);
          }
        }
        setSelectedPropertyName(property_name);
        setSelectedFloorType(tableFloorType);
        setSelectedPitName(floor_name);
        await fetchFloors(property_name);
        await fetchPits(tableFloorType, property_name);
        await fetchTables(tableFloorType, floor_name, property_name);
      } else {
        toast.error('Selected table data not found.');
      }
    } catch (error) {
      console.error('Error during table selection:', error);
      toast.error('Failed to load table data. Please try again.');
    } finally {
      setIsLoadingTables(false);
      console.log('Completed table data fetch.');
    }
  };

  // **Handler for right-click on a table item**
  const handleTableItemContextMenu = (e, tableId, tableName) => {
    e.preventDefault();
    setContextMenuType("tableItem");
    handleTableSelection(tableId, tableName);
    setRightClickedTableId(tableId);
    setContextMenu({ visible: true, x: e.pageX, y: e.pageY });
  };

  // **Clear the selected table filter**
  const clearTableFilter = () => {
    setSelectedTableFilter("");
    setSelectedTableId("");
    setSelectedTableName("");
  };

  // **Handle Drag End Event for Drag and Drop**
  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const items = Array.from(filteredTables);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      setFilteredTables(items);
    },
    [filteredTables]
  );

  // **Handle Submit Configuration**
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const minBetInt = parseInt(minBet, 10);
      const maxBetInt = parseInt(maxBet, 10);
      if (isNaN(minBetInt) || isNaN(maxBetInt)) {
        toast.error('Please enter valid integer values for Min Bet and Max Bet');
        return;
      }
      const template = selectedImage !== null ? `template${selectedImage}` : 'default';
      const gameData = {
        game_name: game,
        min_bet: minBetInt,
        max_bet: maxBetInt,
        is_online: isOnline,
        template: template,
      };
      const filePath = `${selectedPropertyName}/${selectedFloorType}/${selectedPitName}/${tableSID}/limits.yaml`;
      const yamlContent = yaml.dump(gameData);
      await axios.post(`${API_BASE_URL}/updateFile`, {
        bucket: customerUID,
        filePath,
        content: yamlContent,
      });
      await axios.post(`${API_BASE_URL}/updateTables`, {
        table_id: selectedTableId,
        current_game: game,
        min_bet: minBetInt,
        max_bet: maxBetInt,
      });
      const { data: updatedRow } = await axios.get(`${API_BASE_URL}/singleTable`, {
        params: { table_id: selectedTableId },
      });
      setFilteredTables((prevTables) =>
        prevTables.map((table) =>
          table.table_id === updatedRow.table_id ? { ...table, ...updatedRow } : table
        )
      );
      setHighlightedRow(selectedTableId);
      toast.success('Device configured successfully!');
    } catch (error) {
      console.error('Error configuring device:', error);
      toast.error('Failed to configure device. Please try again.');
    } finally {
      setTimeout(() => setHighlightedRow(null), 2000);
      setIsConfigureModalOpen(false);
      setIsLoading(false);
    }
  };

  // **Handle Configure Device - Open Configure Device Modal**
  const handleConfigureDevice = async () => {
    console.log('Configure Device button clicked');
    if (selectedTableId) {
      const selectedTableData = allTables.find((table) => table.table_id === selectedTableId);
      if (selectedTableData) {
        const { table_sid, customer_uid } = selectedTableData;
        const bucket = customerUID;
        const property_name = selectedPropertyName;
        const floor_type = selectedFloorType;
        const floor_name = selectedPitName;
        console.log('Bucket:', bucket);
        console.log('Property Name:', property_name);
        console.log('Floor Type:', floor_type);
        console.log('Floor Name:', floor_name);
        console.log('Table SID:', table_sid);
        console.log('Customer UID:', customer_uid);
        if (bucket && property_name && floor_type && floor_name && table_sid && customer_uid) {
          await checkAndHandleFile(bucket, property_name, floor_type, floor_name, table_sid);
          setIsConfigureModalOpen(true);
        } else {
          toast.error('Missing necessary data to configure the device.');
        }
      } else {
        toast.error('Selected table data not found.');
      }
    } else {
      toast.error('Please select a device to configure.');
    }
  };

  // **Handle Remove Device**
  const handleRemoveDevice = async () => {
    console.log('Remove Device button clicked');
    if (selectedTableId) {
      if (window.confirm(`Are you sure you want to remove device "${selectedTableName}"?`)) {
        setIsLoading(true);
        try {
          await axios.post(`${API_BASE_URL}/removeDevice`, { table_id: selectedTableId });
          console.log(`Device "${selectedTableName}" removed.`);
          toast.success(`Device "${selectedTableName}" removed successfully.`);
          setSelectedTableId('');
          setSelectedTableName('');
          setTableSID('');
          setCustomerUID('');
          await fetchAllTables();
        } catch (error) {
          console.error('Error removing device:', error);
          toast.error('Failed to remove device. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    } else {
      toast.error('Please select a device to remove.');
    }
  };

  // **Open context menu for a table item**
  const handleContextMenu = (e, tableId, tableName) => {
    handleTableItemContextMenu(e, tableId, tableName);
  };

  // **Handle Configure from context menu (for table items)**
  const handleConfigureContext = () => {
    setSelectedTableId(rightClickedTableId);
    handleConfigureDevice();
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  // **Handle Remove from context menu (for table items)**
  const handleRemoveContext = () => {
    setSelectedTableId(rightClickedTableId);
    handleRemoveDevice();
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  // **Close context menu when clicking outside**
  useEffect(() => {
    const handleClickOutside = () => setContextMenu({ visible: false, x: 0, y: 0 });
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // **Effect to filter and sort tables based on selections, search query, and sort configuration**
  useEffect(() => {
    let filtered = allTables;
    if (activeFilters.propertyName) {
      filtered = filtered.filter((table) => table.property_name === activeFilters.propertyName);
    }
    if (activeFilters.floorType) {
      filtered = filtered.filter((table) => table.floor_type === activeFilters.floorType);
    }
    if (activeFilters.pitName) {
      filtered = filtered.filter((table) => table.floor_name === activeFilters.pitName);
    }
    if (selectedTableFilter !== "") {
      filtered = filtered.filter((table) => table.table_id === selectedTableFilter);
    } else if (searchQuery.trim()) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter((table) =>
        Object.values(table).some((value) => String(value).toLowerCase().includes(lowercasedQuery))
      );
    }
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key] ?? '';
        const bValue = b[sortConfig.key] ?? '';
        if (sortConfig.key === 'table_name') {
          return sortConfig.direction === 'ascending'
            ? getNumericValue(a.table_name) - getNumericValue(b.table_name)
            : getNumericValue(b.table_name) - getNumericValue(a.table_name);
        } else {
          const isNumeric = !isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue));
          if (isNumeric) {
            return sortConfig.direction === 'ascending'
              ? parseFloat(aValue) - parseFloat(bValue)
              : parseFloat(bValue) - parseFloat(aValue);
          } else {
            return sortConfig.direction === 'ascending'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
        }
      });
    } else {
      filtered = [...filtered].sort((a, b) => {
        return getNumericValue(a.table_name) - getNumericValue(b.table_name);
      });
    }
    setFilteredTables(filtered);
  }, [allTables, activeFilters, searchQuery, sortConfig, selectedTableFilter]);

  // **Sorting handler**
  const handleSort = (columnKey) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig.key === columnKey && prevConfig.direction === 'ascending'
          ? 'descending'
          : 'ascending';
      return { key: columnKey, direction: newDirection };
    });
  };

  return (
    <div className="configure-device-container" onContextMenu={(e) => e.preventDefault()}>
      {/* **Loading Indicator Overlay** */}
      <LoadingIndicator isLoading={showLoading} />

      {/* **Toast Container for Notifications** */}
      <ToastContainer />

      {/* **Sidebar** */}
      <div className={`collapsible-section-container ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <button
          className="toggle-container-button"
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          aria-label={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isSidebarCollapsed ? '>' : '<'}
        </button>

        {!isSidebarCollapsed && (
          <div className="sidebar-content">
            {/* Property Section */}
            <CollapsibleSection
              title="Property"
              isOpen={!isPropertyCollapsed}
              toggleOpen={() => setIsPropertyCollapsed(!isPropertyCollapsed)}
              selectedOption={selectedPropertyName}
            >
              <ul className="option-list">
                {properties.map((property, index) => (
                  <li
                    key={index}
                    title={property.property_name}
                    className={`option-item ${selectedPropertyName === property.property_name ? 'selected' : ''}`}
                    onClick={() => handlePropertySelection(property.property_name)}
                  >
                    {property.property_name}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>

            {/* Floor Section */}
            <CollapsibleSection
              title="Floor"
              isOpen={!isFloorCollapsed && selectedPropertyName !== ''}
              toggleOpen={() => setIsFloorCollapsed(!isFloorCollapsed)}
              selectedOption={selectedFloorType}
            >
              {selectedPropertyName ? (
                <ul className="option-list">
                  {floors.map((floor, index) => (
                    <li
                      key={index}
                      title={floor.floor_type}
                      className={`option-item ${selectedFloorType === floor.floor_type ? 'selected' : ''}`}
                      onClick={() => handleFloorSelection(floor.floor_type)}
                    >
                      {floor.floor_type}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Please select a property first.</p>
              )}
            </CollapsibleSection>

            {/* Pit Section */}
            <CollapsibleSection
              title="Pit"
              isOpen={!isPitCollapsed && selectedFloorType !== ''}
              toggleOpen={() => setIsPitCollapsed(!isPitCollapsed)}
              selectedOption={selectedPitName}
            >
              {selectedFloorType ? (
                <ul className="option-list">
                  {pits.map((pit, index) => (
                    <li
                      key={index}
                      className={`option-item ${selectedPitName === pit.floor_name ? 'selected' : ''}`}
                      onClick={() => handlePitSelection(pit.floor_name)}
                    >
                      {pit.floor_name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Please select a floor first.</p>
              )}
            </CollapsibleSection>

            {/* Table Section with Pagination */}
            <CollapsibleSection
              title={<span>Table</span>}
              isOpen={!isTableCollapsed && selectedPitName !== ''}
              toggleOpen={() => setIsTableCollapsed(!isTableCollapsed)}
              selectedOption={selectedTableName}
            >
              {selectedPitName ? (
                <div className="table-dropdown-container">
                  <ul className="option-list">
                    {paginatedSidebarTables.map((table, index) => (
                      <li
                        key={index}
                        className={`option-item ${selectedTableId === table.table_id ? 'selected' : ''}`}
                        onClick={() => handleTableSelection(table.table_id, table.table_name)}
                        onContextMenu={(e) => handleTableItemContextMenu(e, table.table_id, table.table_name)}
                      >
                        {table.table_name}
                      </li>
                    ))}
                  </ul>
                  {selectedTableId && (
                    <div className="clear-table-container">
                      <button className="clear-table-button" onClick={clearTableFilter}>
                        Clear Table
                      </button>
                    </div>
                  )}
                  {tables.length > sidebarItemsPerPage && (
                    <div className="sidebar-pagination">
                      <button
                        onClick={() => setSidebarTablePage((prev) => Math.max(prev - 1, 1))}
                        disabled={sidebarTablePage === 1}
                      >
                        ‹
                      </button>
                      <span>
                        Page {sidebarTablePage} of {totalSidebarPages || 1}
                      </span>
                      <button
                        onClick={() => setSidebarTablePage((prev) => Math.min(prev + 1, totalSidebarPages))}
                        disabled={sidebarTablePage === totalSidebarPages}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p>Please select a pit first.</p>
              )}
            </CollapsibleSection>
          </div>
        )}
      </div>

      {/* **Main Content** */}
      <div className={`configure-device-main ${isSidebarCollapsed ? 'expanded' : ''}`}>
        {/* Search and Action Buttons */}
        <div className="search-action-container">
          <input
            type="text"
            className="search-bar"
            placeholder="Search devices..."
            onChange={handleSearchInputChange}
          />
          <div className="action-buttons">
            {/* Removed the "New Device" button */}
            <button type="button" className="action-button configure-device" onClick={handleConfigureDevice}>
              Configure Device
            </button>
            <button type="button" className="action-button remove-device" onClick={handleRemoveDevice}>
              Remove Device
            </button>
          </div>
        </div>

        {/* Table Display */}
        <div className="table-container">
          {!showLoading && filteredTables.length === 0 ? (
            <p>No devices found.</p>
          ) : (
            <table className="devices-table">
              <thead>
                <tr>
                  {[
                    'property_name',
                    'floor_type',
                    'floor_name',
                    'table_name',
                    'current_game',
                    'min_bet',
                    'max_bet',
                    'active_screen',
                  ].map((column) => (
                    <th
                      key={column}
                      onClick={() => handleSort(column)}
                      className={`sortable-header ${sortConfig.key === column ? 'sorted' : ''}`}
                    >
                      <span>
                        {column.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        {sortConfig.key === column && (
                          <span className="sort-arrow">
                            {sortConfig.direction === 'ascending' ? '▲' : '▼'}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentTables.map((table) => (
                  <tr
                    key={table.table_id}
                    className={`table-row ${highlightedRow === table.table_id ? 'highlighted-row' : ''} ${
                      selectedTableId === table.table_id ? 'selected-row' : ''
                    }`}
                    onClick={() => handleTableSelection(table.table_id, table.table_name)}
                    onContextMenu={(e) => handleContextMenu(e, table.table_id, table.table_name)}
                  >
                    <td>{table.property_name}</td>
                    <td>{table.floor_type}</td>
                    <td>{table.floor_name}</td>
                    <td>{table.table_name}</td>
                    <td>{table.current_game || 'N/A'}</td>
                    <td>{table.min_bet || 'N/A'}</td>
                    <td>{table.max_bet || 'N/A'}</td>
                    <td>{table.assigned_screen || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {filteredTables.length > 0 && (
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-button"
                aria-label="Go to first page"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
                aria-label="Go to previous page"
              >
                ‹
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
                aria-label="Go to next page"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-button"
                aria-label="Go to last page"
              >
                »
              </button>
            </div>
          )}
        </div>

        {/* Configure Device Modal */}
        {isConfigureModalOpen && (
          <ConfigureDeviceModal
            isOpen={isConfigureModalOpen}
            onRequestClose={() => setIsConfigureModalOpen(false)}
            isOnline={isOnline}
            setIsOnline={setIsOnline}
            game={game}
            games={games}
            setGame={setGame}
            minBet={minBet}
            setMinBet={setMinBet}
            maxBet={maxBet}
            setMaxBet={setMaxBet}
            submittedMinBet={submittedMinBet}
            submittedMaxBet={submittedMaxBet}
            handleSubmit={handleSubmit}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
          />
        )}
      </div>

      {/* **Context Menu** */}
      {contextMenu.visible && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x, position: 'absolute' }}
        >
          {contextMenuType === "tableItem" ? (
            <>
              <button onClick={handleConfigureContext}>Configure</button>
              <button onClick={handleRemoveContext}>Remove</button>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ConfigureDevice;
